import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { NotificationSettingsService } from '../api/notification-settings-service';
import type {
  NotificationSettingsChanges,
  NotificationSettingsResponse,
} from '../types/notification-settings';

export const notificationSettingsQueryKeys = {
  all: ['notification-settings'] as const,
  global: ['notification-settings', 'global'] as const,
  byAccount: (accountId: string | null) => ['notification-settings', 'account', accountId] as const,
};

export function useGlobalNotificationSettings(): UseQueryResult<NotificationSettingsResponse> {
  return useQuery({
    queryKey: notificationSettingsQueryKeys.global,
    queryFn: () => NotificationSettingsService.getGlobal(),
  });
}

export function useAccountNotificationSettings(
  accountId: string | null,
): UseQueryResult<NotificationSettingsResponse> {
  return useQuery({
    queryKey: notificationSettingsQueryKeys.byAccount(accountId),
    queryFn: () => NotificationSettingsService.getForAccount(accountId as string),
    enabled: Boolean(accountId),
  });
}

export function useSaveGlobalNotificationSettings(): UseMutationResult<
  NotificationSettingsResponse,
  Error,
  NotificationSettingsChanges
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (settings: NotificationSettingsChanges) =>
      NotificationSettingsService.saveGlobal(settings),
    onSuccess: (response) => {
      queryClient.setQueryData(notificationSettingsQueryKeys.global, response);
      queryClient.invalidateQueries({ queryKey: ['notification-settings', 'account'] });
    },
  });
}

export function useSaveAccountNotificationSettings(
  accountId: string,
): UseMutationResult<NotificationSettingsResponse, Error, NotificationSettingsChanges> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (settings: NotificationSettingsChanges) =>
      NotificationSettingsService.saveForAccount(accountId, settings),
    onSuccess: (response) => {
      queryClient.setQueryData(notificationSettingsQueryKeys.byAccount(accountId), response);
    },
  });
}

export function useResetAccountNotificationSettings(
  accountId: string,
): UseMutationResult<NotificationSettingsResponse, Error, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => NotificationSettingsService.resetAccount(accountId),
    onSuccess: (response) => {
      queryClient.setQueryData(notificationSettingsQueryKeys.byAccount(accountId), response);
    },
  });
}
